import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  TextField,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import {
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
  harmonateInputLabelProps,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const LINE_STATUSES = ['ACTIVE', 'INACTIVE'];

const parseLookupItems = (data) => (Array.isArray(data?.list_items) ? data.list_items : []);

const defaultRuleForm = {
  operational_rule_id: '',
  labor_standard_name: '',
  demand_driver: '',
  standard_value: '',
  standard_unit: '',
  calculation_method: '',
  example_description: '',
  status: 'ACTIVE',
  effective_from_date: '',
  effective_to_date: '',
};

const mapRuleRow = (item) => ({
  id: item.operational_rule_id || item.operationalRuleId,
  operational_rule_id: item.operational_rule_id || item.operationalRuleId,
  labor_standard_name: item.labor_standard_name || item.laborStandardName || '',
  demand_driver: item.demand_driver || item.demandDriver || '',
  standard_value: item.standard_value ?? item.standardValue ?? 0,
  standard_unit: item.standard_unit || item.standardUnit || '',
  calculation_method: item.calculation_method || item.calculationMethod || '',
  example_description: item.example_description || item.exampleDescription || '',
  status: item.status || 'ACTIVE',
  effective_from_date: (item.effective_from_date || item.effectiveFromDate || '').slice(0, 10),
  effective_to_date: (item.effective_to_date || item.effectiveToDate || '').slice(0, 10),
});

const labelForCode = (options, code) => options.find((opt) => opt.code === code)?.label || code;

const PositionOperationRulesSection = forwardRef(({
  positionRow,
  token,
  baseUrl,
  tenantId,
  propertyId,
  showError,
  showSuccess,
  active = true,
  hideToolbar = false,
  onSelectionChange,
}, ref) => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.WORKFORCE_OPTIMIZATION);
  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState([]);
  const [selection, setSelection] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [ruleForm, setRuleForm] = useState(defaultRuleForm);
  const [demandDriverOptions, setDemandDriverOptions] = useState([]);
  const [standardUnitOptions, setStandardUnitOptions] = useState([]);
  const [calcMethodOptions, setCalcMethodOptions] = useState([]);

  const rolePositionId = positionRow?.role_position_id || positionRow?.id;
  const departmentName = positionRow?.department || '';

  const authHeaders = useMemo(
    () => ({
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    }),
    [token]
  );

  const loadLookups = useCallback(async () => {
    const tenantQuery = tenantId ? `&tenant_id=${encodeURIComponent(tenantId)}` : '';
    const [demandRes, unitRes, calcRes] = await Promise.all([
      fetch(`${baseUrl}conductor/lookup-list?list_code=DEMAND_DRIVER${tenantQuery}`, { headers: authHeaders }),
      fetch(`${baseUrl}conductor/lookup-list?list_code=STANDARD_UNIT${tenantQuery}`, { headers: authHeaders }),
      fetch(`${baseUrl}conductor/lookup-list?list_code=OPERATION_CALC_METHOD${tenantQuery}`, { headers: authHeaders }),
    ]);
    if (demandRes.ok) {
      setDemandDriverOptions(parseLookupItems(await demandRes.json()));
    }
    if (unitRes.ok) {
      setStandardUnitOptions(parseLookupItems(await unitRes.json()));
    }
    if (calcRes.ok) {
      setCalcMethodOptions(parseLookupItems(await calcRes.json()));
    }
  }, [authHeaders, baseUrl, tenantId]);

  const loadRules = useCallback(async () => {
    if (!rolePositionId || !tenantId || !propertyId) {
      setRules([]);
      return;
    }
    setLoading(true);
    try {
      const query = new URLSearchParams({
        tenant_id: tenantId || '',
        property_id: propertyId || '',
        role_position_id: rolePositionId,
      });
      const res = await fetch(`${baseUrl}conductor/workforce-operational-labor-rule?${query}`, {
        headers: authHeaders,
      });
      if (!res.ok) throw new Error('Failed to load operation rules');
      const data = await res.json();
      setRules((Array.isArray(data) ? data : []).map(mapRuleRow));
    } catch (error) {
      showError(error.message || 'Failed to load operation rules');
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, baseUrl, propertyId, rolePositionId, showError, tenantId]);

  useEffect(() => {
    if (!active) return;
    setSelection([]);
    loadLookups();
    loadRules();
  }, [active, loadLookups, loadRules]);

  const selectedRow = useMemo(
    () => rules.find((row) => row.id === selection[0]) || null,
    [rules, selection]
  );

  useEffect(() => {
    if (onSelectionChange) onSelectionChange(selectedRow);
  }, [onSelectionChange, selectedRow]);

  const columns = useMemo(
    () => [
      { field: 'labor_standard_name', headerName: 'Rule Name', flex: 1, minWidth: 180, resizable: true },
      {
        field: 'demand_driver',
        headerName: 'Demand Driver',
        width: 140,
        resizable: true,
        valueFormatter: (params) => labelForCode(demandDriverOptions, params.value),
      },
      { field: 'standard_value', headerName: 'Standard', width: 90, resizable: true },
      {
        field: 'standard_unit',
        headerName: 'Unit',
        width: 100,
        resizable: true,
        valueFormatter: (params) => labelForCode(standardUnitOptions, params.value),
      },
      {
        field: 'calculation_method',
        headerName: 'Calculation',
        width: 150,
        resizable: true,
        valueFormatter: (params) => labelForCode(calcMethodOptions, params.value),
      },
      { field: 'status', headerName: 'Status', width: 90, resizable: true },
    ],
    [calcMethodOptions, demandDriverOptions, standardUnitOptions]
  );

  const openAdd = () => {
    if (!canAdd) return;
    setRuleForm({
      ...defaultRuleForm,
      demand_driver: demandDriverOptions[0]?.code || '',
      standard_unit: standardUnitOptions[0]?.code || '',
      calculation_method: calcMethodOptions[0]?.code || '',
      effective_from_date: new Date().toISOString().slice(0, 10),
    });
    setFormOpen(true);
  };

  const openEdit = () => {
    if (!canEdit || !selectedRow) return;
    setRuleForm({
      operational_rule_id: selectedRow.operational_rule_id,
      labor_standard_name: selectedRow.labor_standard_name,
      demand_driver: selectedRow.demand_driver,
      standard_value: selectedRow.standard_value,
      standard_unit: selectedRow.standard_unit,
      calculation_method: selectedRow.calculation_method,
      example_description: selectedRow.example_description,
      status: selectedRow.status,
      effective_from_date: selectedRow.effective_from_date,
      effective_to_date: selectedRow.effective_to_date,
    });
    setFormOpen(true);
  };

  const saveRule = async () => {
    const body = {
      tenantId,
      propertyId,
      rolePositionId,
      rolePositionName: positionRow?.role_name || '',
      departmentName,
      laborStandardName: ruleForm.labor_standard_name.trim(),
      demandDriver: ruleForm.demand_driver,
      standardValue: Number(ruleForm.standard_value) || 0,
      standardUnit: ruleForm.standard_unit,
      calculationMethod: ruleForm.calculation_method,
      exampleDescription: ruleForm.example_description || null,
      status: ruleForm.status,
      effectiveFromDate: ruleForm.effective_from_date,
      effectiveToDate: ruleForm.effective_to_date || null,
    };

    const isEdit = Boolean(ruleForm.operational_rule_id);
    const url = isEdit
      ? `${baseUrl}conductor/workforce-operational-labor-rule/${ruleForm.operational_rule_id}`
      : `${baseUrl}conductor/workforce-operational-labor-rule`;
    const res = await fetch(url, {
      method: isEdit ? 'PUT' : 'POST',
      headers: authHeaders,
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to save operation rule');
    }
    setFormOpen(false);
    showSuccess(isEdit ? 'Operation rule updated.' : 'Operation rule added.');
    loadRules();
  };

  const deleteRule = async () => {
    if (!selectedRow) return;
    const res = await fetch(
      `${baseUrl}conductor/workforce-operational-labor-rule/${selectedRow.operational_rule_id}?tenant_id=${encodeURIComponent(tenantId || '')}&property_id=${encodeURIComponent(propertyId || '')}`,
      {
        method: 'DELETE',
        headers: authHeaders,
      }
    );
    if (!res.ok && res.status !== 204) throw new Error('Delete failed');
    setSelection([]);
    showSuccess('Operation rule removed.');
    loadRules();
  };

  useImperativeHandle(ref, () => ({
    openAdd,
    openEdit: () => openEdit(),
    openDelete: () => {
      if (!canDelete) return;
      deleteRule().catch((e) => showError(e.message));
    },
  }), [selectedRow, showError]);

  return (
    <>
      <Box sx={{ px: hideToolbar ? 0 : 2, pt: hideToolbar ? 0 : 1, pb: hideToolbar ? 0 : 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
        <Paper elevation={0} sx={{ flex: 1, borderRadius: 0, overflow: 'hidden', boxShadow: 'none', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <DataGridPro
              rows={rules}
              columns={columns}
              columnResizeMode="onEnd"
              resizable
              getRowId={(row) => row.id}
              rowHeight={44}
              loading={loading}
              rowSelectionModel={selection}
              onRowSelectionModelChange={setSelection}
              pagination
              pageSize={100}
              sx={{
                ...harmonateDataGridSx,
                border: 'none',
                flex: 1,
                minHeight: 0,
                '& .MuiDataGrid-cell': { cursor: 'pointer' },
              }}
            />
          </Box>
        </Paper>
      </Box>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{ruleForm.operational_rule_id ? 'Edit Operation Rule' : 'Add Operation Rule'}</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx, overflow: 'visible' }}>
          <TextField
            label="Rule Name"
            value={ruleForm.labor_standard_name}
            onChange={(e) => setRuleForm((prev) => ({ ...prev, labor_standard_name: e.target.value }))}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            placeholder="e.g. Minutes per Occupied Room"
          />
          <TextField
            select
            label="Demand Driver"
            value={ruleForm.demand_driver}
            onChange={(e) => setRuleForm((prev) => ({ ...prev, demand_driver: e.target.value }))}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            SelectProps={{ MenuProps: harmonateSelectMenuProps }}
          >
            {demandDriverOptions.map((opt) => (
              <MenuItem key={opt.code} value={opt.code}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <TextField
              label="Standard Value"
              type="number"
              value={ruleForm.standard_value}
              onChange={(e) => setRuleForm((prev) => ({ ...prev, standard_value: e.target.value }))}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              select
              label="Unit"
              value={ruleForm.standard_unit}
              onChange={(e) => setRuleForm((prev) => ({ ...prev, standard_unit: e.target.value }))}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ MenuProps: harmonateSelectMenuProps }}
            >
              {standardUnitOptions.map((opt) => (
                <MenuItem key={opt.code} value={opt.code}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <TextField
            select
            label="Calculation Method"
            value={ruleForm.calculation_method}
            onChange={(e) => setRuleForm((prev) => ({ ...prev, calculation_method: e.target.value }))}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            SelectProps={{ MenuProps: harmonateSelectMenuProps }}
          >
            {calcMethodOptions.map((opt) => (
              <MenuItem key={opt.code} value={opt.code}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Example / Description"
            value={ruleForm.example_description}
            onChange={(e) => setRuleForm((prev) => ({ ...prev, example_description: e.target.value }))}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            placeholder="e.g. 28 minutes per occupied room"
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <TextField
              label="Effective From"
              type="date"
              value={ruleForm.effective_from_date}
              onChange={(e) => setRuleForm((prev) => ({ ...prev, effective_from_date: e.target.value }))}
              fullWidth
              size="small"
              InputLabelProps={{ ...harmonateInputLabelProps, shrink: true }}
            />
            <TextField
              label="Effective To"
              type="date"
              value={ruleForm.effective_to_date}
              onChange={(e) => setRuleForm((prev) => ({ ...prev, effective_to_date: e.target.value }))}
              fullWidth
              size="small"
              InputLabelProps={{ ...harmonateInputLabelProps, shrink: true }}
            />
          </Box>
          <TextField
            select
            label="Status"
            value={ruleForm.status}
            onChange={(e) => setRuleForm((prev) => ({ ...prev, status: e.target.value }))}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            SelectProps={{ MenuProps: harmonateSelectMenuProps }}
          >
            {LINE_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setFormOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button variant="contained" onClick={() => saveRule().catch((e) => showError(e.message))} sx={harmonateProceedButtonSx}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default PositionOperationRulesSection;
